/**
 * Plating selector interactions (PDP):
 *  - radio selection state + selected-name label
 *  - "View plating details" modal (shows the selected plating's section)
 *
 * Price approach: the product price element is NOT modified by plating — it
 * always shows the variant's own price. The paid platings display their
 * "+ amount" on the option itself, and checkout charges the add-on as a
 * separate line, so adding it into the headline price double-announced it.
 * window.DuavaaPlating.addonValue() stays for the Offer Engine and now always
 * reports 0 (nothing plating-related is in the displayed price to subtract).
 */
(function () {
  'use strict';

  // Mirrors offer-promos.js parsing: "$1,500.00" / "AED 100.00" / "₹1,500.00".
  // Also captures the raw string + decimal places so we can preserve the
  // theme's exact formatting when re-rendering the total.
  function parsePriceText(text) {
    var raw = String(text || '').trim();
    var m = raw.match(/^([^\d]*)([\d,]+(?:\.(\d+))?)/);
    if (!m) return null;
    var value = parseFloat(m[2].replace(/,/g, ''));
    if (!isFinite(value)) return null;
    var symbol = m[1].trim();
    if (!symbol) symbol = '$';
    else if (/[A-Za-z]$/.test(symbol)) symbol += ' '; // "AED " keeps a space; "$" hugs
    // prefix = the exact symbol + separator the theme rendered ("$ ", "Rs. ",
    // "AED "). Reused verbatim so the product price keeps its symbol spacing.
    return { symbol: symbol, prefix: m[1], value: value, decimals: m[3] ? m[3].length : 0, raw: raw };
  }

  // Group the integer part with commas, keeping the base's decimal places.
  function formatMoney(symbol, value, decimals) {
    var fixed = Number(value).toFixed(decimals || 0);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return symbol + parts.join('.');
  }

  var activeAddon = 0; // market-currency add-on of the current selection (for offers)

  function optAddonValue(input) {
    if (!input) return 0;
    var label = input.closest('.product__plating__opt');
    var priceEl = label && label.querySelector('[data-plating-addon-price]');
    if (!priceEl) return 0; // silver / included
    var parsed = parsePriceText(priceEl.textContent);
    return parsed ? parsed.value : 0;
  }

  function sectionFor(selector) {
    return selector.closest('form.product-form') ||
           selector.closest('[data-section-type]') ||
           selector.closest('.shopify-section') ||
           document;
  }

  function priceElFor(selector) {
    var scope = sectionFor(selector);
    return (scope.querySelector && scope.querySelector('[data-product-price]')) ||
           document.querySelector('[data-product-price]');
  }

  // Controller per selector (usually one PDP; quick-views may have several).
  function initSelector(selector) {
    if (selector.__platingInit) return;
    selector.__platingInit = true;

    // Plating amounts are whole numbers (rounded up); show them without the
    // ".00" (e.g. "+Rs. 2,500" / "+$25"). Strip only the trailing decimals so
    // the theme's currency symbol and spacing are preserved exactly.
    selector.querySelectorAll('[data-plating-addon-price]').forEach(function (el) {
      el.textContent = String(el.textContent).replace(/\.\d+$/, '');
    });

    var priceEl = priceElFor(selector);
    var selectedName = selector.querySelector('[data-plating-selected-name]');
    var details = sectionFor(selector).querySelector
      ? sectionFor(selector).querySelector('[data-plating-details]')
      : null;
    if (!details) details = document.querySelector('[data-plating-details]');

    // Clean base captured from the theme's render; refreshed on variant change.
    var cleanBase = priceEl ? parsePriceText(priceEl.textContent) : null;

    function currentInput() {
      return selector.querySelector('input[name="plating_option"]:checked');
    }

    function applyPrice() {
      if (!priceEl || !cleanBase) return;
      // Constant price: restore the theme's own text (a no-op unless an older
      // version of this script had added the plating amount into it).
      activeAddon = 0;
      priceEl.textContent = cleanBase.raw;
    }

    function selectKey(key) {
      var inputs = selector.querySelectorAll('input[name="plating_option"]');
      inputs.forEach(function (inp) {
        var on = inp.getAttribute('data-plating-key') === key;
        inp.checked = on;
        var opt = inp.closest('.product__plating__opt');
        if (opt) opt.classList.toggle('is-selected', on);
      });
      var active = selector.querySelector('input[name="plating_option"]:checked');
      if (selectedName && active) selectedName.textContent = active.getAttribute('data-plating-name') || '';
      if (details) showDetail(key);
      applyPrice();
    }

    function showDetail(key) {
      var sections = details.querySelectorAll('[data-plating-detail]');
      sections.forEach(function (s) {
        s.hidden = s.getAttribute('data-plating-detail') !== key;
      });
    }

    selector.addEventListener('change', function (e) {
      var inp = e.target.closest('input[name="plating_option"]');
      if (inp) selectKey(inp.getAttribute('data-plating-key'));
    });

    // Open details modal (shows the currently selected plating).
    var openBtn = selector.querySelector('[data-plating-details-open]');
    if (openBtn && details) {
      openBtn.addEventListener('click', function () {
        var active = currentInput();
        showDetail(active ? active.getAttribute('data-plating-key') : 'silver');
        details.hidden = false;
        document.documentElement.style.overflow = 'hidden';
      });
      details.querySelectorAll('[data-plating-details-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          details.hidden = true;
          document.documentElement.style.overflow = '';
        });
      });
      document.addEventListener('keydown', function (ev) {
        if (ev.key === 'Escape' && !details.hidden) {
          details.hidden = true;
          document.documentElement.style.overflow = '';
        }
      });
    }

    // The theme rewrites [data-product-price] on variant change with the clean
    // base; recapture it (applyPrice leaves the theme's text as-is).
    var section = sectionFor(selector);
    var onVariant = function () {
      setTimeout(function () {
        var el = priceElFor(selector);
        if (el) { priceEl = el; cleanBase = parsePriceText(el.textContent); applyPrice(); }
      }, 60);
    };
    if (section && section.addEventListener) section.addEventListener('theme:variant:change', onVariant);
    document.addEventListener('theme:variant:change', onVariant);

    // Initial state (default = silver, addon 0 → base unchanged).
    var initial = currentInput();
    selectKey(initial ? initial.getAttribute('data-plating-key') : 'silver');
  }

  function initAll() {
    document.querySelectorAll('[data-plating-selector]').forEach(initSelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Re-init for late-rendered PDPs (quick view / section re-render).
  document.addEventListener('theme:cart:refresh', initAll);
  document.addEventListener('shopify:section:load', initAll);

  // Offer Engine reads the plating add-on so it can exclude it from offer math.
  window.DuavaaPlating = {
    addonValue: function () { return activeAddon; },
  };
})();
